'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { MessageCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OutreachCompose } from '@/components/outreach/outreach-compose';
import {
  outreachComposeParamsKey,
  type OutreachComposeOpenParams,
} from '@/components/outreach/outreach-compose-open-params';
import type { PendingChatDraft } from '@/components/outreach/pending-chat-draft';

type OutreachComposeContextValue = {
  open: (params?: OutreachComposeOpenParams) => void;
  close: () => void;
  isOpen: boolean;
  stageChatDraft: (draft: PendingChatDraft) => void;
  /** Increments when a draft is staged — room chat listens to apply it. */
  chatDraftSignal: number;
  takePendingChatDraft: () => PendingChatDraft | null;
};

const OutreachComposeContext = createContext<OutreachComposeContextValue | null>(null);

export function useOutreachCompose(): OutreachComposeContextValue {
  const ctx = useContext(OutreachComposeContext);
  if (!ctx) {
    throw new Error('useOutreachCompose must be used within OutreachComposeProvider');
  }
  return ctx;
}

export function OutreachComposeProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState<OutreachComposeOpenParams>({});
  const [chatDraftSignal, setChatDraftSignal] = useState(0);
  const pendingChatDraftRef = useRef<PendingChatDraft | null>(null);

  const openOutreach = useCallback((next?: OutreachComposeOpenParams) => {
    setParams(next ?? {});
    setOpen(true);
  }, []);

  const closeOutreach = useCallback(() => {
    setOpen(false);
  }, []);

  const stageChatDraft = useCallback((draft: PendingChatDraft) => {
    pendingChatDraftRef.current = draft;
    setChatDraftSignal((count) => count + 1);
  }, []);

  const takePendingChatDraft = useCallback(() => {
    const draft = pendingChatDraftRef.current;
    pendingChatDraftRef.current = null;
    return draft;
  }, []);

  const value = useMemo(
    () => ({
      open: openOutreach,
      close: closeOutreach,
      isOpen: open,
      stageChatDraft,
      chatDraftSignal,
      takePendingChatDraft,
    }),
    [openOutreach, closeOutreach, open, stageChatDraft, chatDraftSignal, takePendingChatDraft]
  );

  const composeKey = outreachComposeParamsKey(open ? params : null);

  return (
    <OutreachComposeContext.Provider value={value}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) closeOutreach();
        }}
      >
        <DialogContent
          className="flex h-[min(92vh,52rem)] max-h-[min(92vh,52rem)] w-[min(100vw-1.5rem,48rem)] max-w-[min(100vw-1.5rem,48rem)] flex-col gap-0 overflow-hidden p-0 sm:rounded-lg"
        >
          <DialogHeader className="shrink-0 border-b px-6 py-4 text-left space-y-1">
            <DialogTitle className="flex items-center gap-2 text-lg pr-8">
              <MessageCircle className="h-5 w-5 text-primary" />
              Send message
            </DialogTitle>
            <DialogDescription>
              Private outreach to a room member — powered by account and deal insights
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <OutreachCompose
              key={composeKey}
              variant="modal"
              initialAccountId={params.accountId}
              initialDealId={params.dealId}
              initialRoomId={params.roomId}
              initialRecipientId={params.recipientId}
              initialUserContext={params.context ?? ''}
              accountName={params.accountName}
              dealName={params.dealName}
              onSent={() => closeOutreach()}
              onViewThread={closeOutreach}
              onStageChatDraft={stageChatDraft}
            />
          </div>
        </DialogContent>
      </Dialog>
    </OutreachComposeContext.Provider>
  );
}
